import isEqual from 'lodash-es/isEqual';

import settingsIcon from '@disy/cadenza-icons/settings.svg';
import type { EventKey } from '@disy/lib/event-util';
import { on, unByKey } from '@disy/lib/event-util';
import { icon } from '@disy/lib/icon';
import { logger } from '@disy/lib/logging';
import { uniqueId } from '@disy/lib/unique-id';
import type { MenuContentElement, MenuItem } from '@disy/ui/menu';
import { closeMenu } from '@disy/ui/menu';
import { MENU_ITEM_SEPARATOR, setupMenuButton } from '@disy/ui/menu';

import type { AttributeList, AttributeListItemSelectedEvent } from 'ui/attribute-list/attribute-list';
import { createAttributeList } from 'ui/attribute-list/attribute-list';

import { Command } from 'cadenza/api-client/workbook-commands';
import { CONDITION_CHANGE } from 'cadenza/conditions/event-types';
import type { QlCondition } from 'cadenza/conditions/ql-condition/ql-condition';
import { isFeatureAvailable } from 'cadenza/features';
import { getStore } from 'cadenza/store/cadenza-store';
import { createOrEditConditionGroup } from 'cadenza/workbook/condition-group/condition-group';
import { conditionHasValues, hasValuesSet, isHidden, supportsReset } from 'cadenza/workbook/conditions/condition-util';
import {
  activatableConditionsBulk,
  canActivateAllConditions,
  canActivateAllConditionsForActiveWorksheet,
  canDeactivateAllConditions,
  canDeactivateAllConditionsForActiveWorksheetOnly,
  deactivatableConditionsBulk,
  hasAffectedViews,
  isDeactivatedIndeterminateForAny,
  setConditionDeactivated,
  setConditionDeactivatedForActiveWorksheet,
} from 'cadenza/workbook/conditions/condition-workbook-utils';
import { openEditSpatialConditionModal } from 'cadenza/workbook/conditions/spatial-condition/open-spatial-condition-modal';
import { findConditionInAnalysisContext } from 'cadenza/workbook/conditions/spatial-condition/spatial-condition-utils';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id.gen';
import type { ConditionDto } from 'cadenza/workbook/model/condition-dto';
import type { ConditionGroupDto } from 'cadenza/workbook/model/condition-group-dto';
import { isConditionGroup } from 'cadenza/workbook/model/condition-group-dto';
import type { ConditionId } from 'cadenza/workbook/model/condition-id.gen';
import {
  isConditionConvertibleToQl,
  isConversionToQlConditionAllowed,
} from 'cadenza/workbook/ql-condition/ql-condition-workbook-utils';
import { canCreateMapContext } from 'cadenza/workbook/state/default-map.state';
import { filterAllAttributesAndHierarchies } from 'cadenza/workbook/workbook-attribute-util';
import { executeCommand } from 'cadenza/workbook/workbook-core-reducers.state';
import {
  conditionsAndGroupsModel,
  getActiveWorksheetId,
  getAllEnrichmentObjecttypeIds,
  getAttributes,
  getConditionById,
  getConditionsAndGroups,
  getGlobalConditionIds,
} from 'cadenza/workbook/workbook-core-selectors.state';
import { isWorkbookFeatureAvailable } from 'cadenza/workbook/workbook-features';
import type { AnalysisContextPanel } from 'cadenza/workbook/workbook-panel/analysis-context/analysis-context-panel';
import type { ConditionsPanelController } from 'cadenza/workbook/workbook-panel/analysis-context/conditions-panel-controller';
import type { DataViewBundleController } from 'cadenza/workbook/workbook-panel/settings/attributes/data-view-bundle-controller';
import { AttributeDropPanel } from 'cadenza/workbook/workbook-panel/settings/attributes/drop-panel/attribute-drop-panel';
import { getWorkbook } from 'cadenza/workbook/workbook-utils';

import i18n from './analysis-context.properties';

export const CONDITION_DRAG_HANDLE = 'condition-drag-handle';
const COMPONENT_NAME = 'd-condition-drop-panel';
const MAX_WIDTH_OPEN_SUBMENU_ON_CLICK = 700;

export class ConditionDropPanel extends AttributeDropPanel {
  #eventKeys?: EventKey[];

  // @ts-expect-error – sub-classing and static typing are irreconcilable in this case
  controller: ConditionsPanelController;

  constructor(controller: ConditionsPanelController) {
    const conditionManagementEnabled = isWorkbookFeatureAvailable(
      getStore().getState(),
      'workbook-condition-management',
    );
    super(controller as unknown as DataViewBundleController, {
      isPanelSectionHeading: true,
      title: i18n('filter-criteria.label'),
      dropAreaText: i18n('filter-criteria.drop.empty.label'),
      sortableStyleClasses: ['analysis-drop-panel', 'd-stack-v', 'space-4'],
      showClearButton: true,
      showPlusButton: conditionManagementEnabled,
      showMoreActionButton: conditionManagementEnabled,
      handleSelector: `.${CONDITION_DRAG_HANDLE}`,
      draggableSelector: '.draggable-condition-element-marker',
      dragAndDropDisabled: !conditionManagementEnabled,
    });
    this.controller = controller;
  }

  override connectedCallback() {
    super.connectedCallback();
    this.classList.add(COMPONENT_NAME);
    this.setAttribute('role', 'group');
    this.#updateClearButtonVisibility();
    this.#eventKeys = [on(this, CONDITION_CHANGE, () => this.#updateClearButtonVisibility())];
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    unByKey(this.#eventKeys);
  }

  override postRender() {
    this.#updateClearButtonVisibility();
  }

  // @ts-expect-error - sub-classing and static typing are irreconcilable in this case
  override needsReRendering(currentRenderAttributes: ConditionDto[], attributes: ConditionDto[]) {
    return !isEqual(currentRenderAttributes, attributes);
  }

  override setupPlusButton(plusButton: HTMLButtonElement) {
    setupMenuButton(plusButton, this.getFilterActions.bind(this), {
      openSubMenuOnClick: () => window.innerWidth <= MAX_WIDTH_OPEN_SUBMENU_ON_CLICK,
    });
    return plusButton;
  }

  override getMoreActions() {
    const disableAdvanceFilterSettings = !getConditionsAndGroups(getStore().getState()).length;
    return [
      ...(isConversionToQlConditionAllowed() ?
        [
          {
            label: i18n('actions.convert-all-to-advanced'),
            disabled: !this.#isConversionOfAllConditionsToQlConditionsEnabled(),
            execute: () => this.controller.convertToQL(this.#conditionsConvertibleToQl),
          },
          MENU_ITEM_SEPARATOR,
        ]
      : []),
      ...createActivateDeactivateFilterActions(
        getGlobalConditionIds(getStore().getState()),
        this.controller.analysisContext,
      ),
      MENU_ITEM_SEPARATOR,
      {
        label: i18n('actions.advanceSettings'),
        icon: icon(settingsIcon),
        execute: () => openAdvancedFilterSettingsDialog(),
        disabled: disableAdvanceFilterSettings,
        tooltip: disableAdvanceFilterSettings ? i18n('actions.advanceSettingsDisabledTooltip') : undefined,
      },
    ];
  }

  #updateClearButtonVisibility() {
    const clearButton = this.querySelector<HTMLButtonElement>('.d-condition-drop-panel--clear');
    if (clearButton) {
      const showClearButton = this.controller.attributes
        .flatMap((conditionOrGroup) =>
          isConditionGroup(conditionOrGroup) ?
            conditionOrGroup.conditions.map((conditionId) => getConditionById(getStore().getState(), conditionId))
          : [conditionOrGroup],
        )
        .some((condition) => supportsReset(condition) && hasValuesSet(condition) && hasAffectedViews(condition.id));
      clearButton.hidden = !showClearButton;
    }
  }

  #setupChangeEvent(attributeList: AttributeList) {
    on(attributeList, 'select', (event: AttributeListItemSelectedEvent) => {
      this.controller.add(event.detail.attribute.id as AttributeId);
      closeMenu();
    });
  }

  async getFilterActions(): Promise<MenuContentElement[]> {
    const analysisContext = getWorkbook().analysisContextPanel;
    const isCondition = (conditionOrGroup: ConditionDto | ConditionGroupDto) =>
      !!analysisContext.getConditionById(conditionOrGroup.id as ConditionId);
    const attributes = filterAllAttributesAndHierarchies((attribute) => this.controller.accepts(attribute).accepts);
    const spatialFilterActionEnabled = canCreateMapContext(getStore().getState());
    const canCreateFilterGroup = conditionsAndGroupsModel(getStore().getState()).some(isCondition);
    return [
      {
        group: [
          ...(isWorkbookFeatureAvailable(getStore().getState(), 'workbook-data-view-content') ?
            [
              {
                label: i18n('actions.filterAttributes'),
                subMenu: () => {
                  const attributeList = createAttributeList(attributes, {
                    enrichmentObjecttypeIds: getAllEnrichmentObjecttypeIds(getStore().getState()),
                  });
                  this.#setupChangeEvent(attributeList);
                  return [attributeList];
                },
              },
            ]
          : []),
        ],
      },
      {
        group: [
          {
            label: i18n('actions.newQLCondition'),
            execute: () => this.addAndEditQlCondition(),
          },
          {
            label: i18n('actions.newQLConditionWithVariables'),
            execute: () => this.addAndEditQlCondition({ withVariables: true }),
          },
          ...(isFeatureAvailable('WORKBOOK_EDIT_SPATIAL_CONDITION') ?
            [
              {
                label: i18n('actions.new-spatial-filter'),
                execute: () => openEditSpatialConditionModal(),
                disabled: !spatialFilterActionEnabled,
                tooltip: spatialFilterActionEnabled ? undefined : i18n('noDefaultMapAvailableTooltip'),
              },
            ]
          : []),
        ],
      },
      {
        group: [
          {
            label: i18n('actions.new-filter-group'),
            disabled: !canCreateFilterGroup,
            execute: () => createOrEditConditionGroup(),
            tooltip: canCreateFilterGroup ? '' : i18n('actions.new-filter-group.disabled'),
          },
        ],
      },
    ];
  }

  addAndEditQlCondition({ withVariables }: { withVariables?: boolean } = {}) {
    const conditionId = uniqueId() as ConditionId;
    const printName = this.#getQlConditionPrintName(withVariables ?? false);
    getStore()
      .dispatch(executeCommand(Command.ADD_QL_CONDITION({ conditionId, printName })))
      .then(() => this.#editCreatedQlCondition(conditionId, { withVariables }))
      .catch(() => logger.error('Could not create new CQL condition'));
  }

  #getQlConditionPrintName(withVariables: boolean) {
    return withVariables ? i18n('qlCondition.defaultName.withVariables') : i18n('qlCondition.defaultName');
  }

  async #editCreatedQlCondition(conditionId: ConditionId, { withVariables }: { withVariables?: boolean }) {
    if (conditionId) {
      const conditionElement = await this.#waitForConditionInAnalysisContext(conditionId);
      if (conditionElement) {
        conditionElement.openEditConditionDialog(withVariables).finally(() => this.#removeEmptyCondition(conditionId));
      }
    }
  }

  #waitForConditionInAnalysisContext(conditionId: ConditionId): Promise<QlCondition> {
    // Need to wait until condition element is added to analysis context UI
    // Previous solution was adding a timeout, this is improved a bit, but still not perfect.
    return new Promise((resolve) => {
      const conditionElement = findConditionInAnalysisContext<QlCondition>(this, conditionId);
      if (conditionElement) {
        resolve(conditionElement);
      } else {
        const eventKey = on(this, 'ql-condition-added', (event) => {
          if (conditionId === (event as CustomEvent).detail.conditionId) {
            unByKey(eventKey);
            resolve(findConditionInAnalysisContext<QlCondition>(this, conditionId)!);
          }
        });
      }
    });
  }

  override hasEnabledMoreActionsButton() {
    return true; // always enabled because of the addEmptyQlCondition action
  }

  #isConversionOfAllConditionsToQlConditionsEnabled() {
    // conversion is enabled only when there's at least one convertible and populated condition and no (populated) QL conditions yet
    return this.#conditionsConvertibleToQl.length > 0 && this.#populatedQlConditions.length === 0;
  }

  get #allConditions() {
    const analysisContext = getWorkbook().analysisContextPanel;
    return (this.controller.attributes as (ConditionDto | ConditionGroupDto)[]).flatMap((conditionOrGroup) =>
      isConditionGroup(conditionOrGroup) ?
        conditionOrGroup.conditions.map((id) => analysisContext.getConditionById(id))
      : [conditionOrGroup],
    );
  }

  get #conditionsConvertibleToQl() {
    const acceptedAttributesForQLFilter = getAttributes(getStore().getState()).filter(
      (attribute) => this.accepts(attribute).accepts,
    );
    return this.#allConditions.filter(
      (condition) =>
        isConditionConvertibleToQl(condition, acceptedAttributesForQLFilter) &&
        conditionHasValues(condition) &&
        !isHidden(condition, getActiveWorksheetId(getStore().getState())),
    );
  }

  get #populatedQlConditions() {
    return this.#allConditions.filter((cond) => cond.type === 'QlCondition' && conditionHasValues(cond));
  }

  #removeEmptyCondition(conditionId: ConditionId) {
    const condition = getConditionById(getStore().getState(), conditionId);
    if (!conditionHasValues(condition)) {
      getStore().dispatch(executeCommand(Command.REMOVE_CONDITION({ conditionId })));
    }
  }
}

function createActivateDeactivateFilterActions(conditionIds: ConditionId[], analysisContext: AnalysisContextPanel) {
  const canToggleDeactivationPerWorksheet = isDeactivatedIndeterminateForAny(conditionIds);
  const canDeactivate = canDeactivateAllConditions(conditionIds);
  const actions: MenuItem[] = [];
  actions.push(
    canToggleDeactivationPerWorksheet ?
      createActivateFilterThisWorksheetAction(conditionIds, analysisContext)
    : createActivateFilterAction(conditionIds, analysisContext),
  );
  if (canDeactivate) {
    actions.push(
      canToggleDeactivationPerWorksheet ?
        createDeactivateFilterThisWorksheetAction(conditionIds, analysisContext)
      : createDeactivateFilterAction(conditionIds, analysisContext),
    );
  }
  return actions;
}

function createDeactivateFilterAction(groupConditionIds: ConditionId[], analysisContext: AnalysisContextPanel) {
  const deactivatableConditions = deactivatableConditionsBulk(groupConditionIds);
  return {
    label: i18n('actions.filters.deactivate'),
    tooltip: i18n('actions.filters.deactivate.tooltip'),
    execute: () => {
      setConditionDeactivated(deactivatableConditions, true)
        .then(() => analysisContext.dispatchEvent(new CustomEvent(CONDITION_CHANGE, { bubbles: true })))
        .catch((error) => logger.error('Failed to rerender group element', error));
    },
  };
}

function createDeactivateFilterThisWorksheetAction(
  groupConditionIds: ConditionId[],
  analysisContext: AnalysisContextPanel,
) {
  const canDeactivateWorksheetOnly = canDeactivateAllConditionsForActiveWorksheetOnly(groupConditionIds);
  const deactivatableConditions = deactivatableConditionsBulk(groupConditionIds);
  return {
    label: i18n('actions.filters.deactivate.thisWorksheet'),
    disabled: !canDeactivateWorksheetOnly,
    tooltip: i18n('actions.filters.deactivate.tooltip'),
    execute: () => {
      setConditionDeactivatedForActiveWorksheet(deactivatableConditions, true)
        .then(() => analysisContext.dispatchEvent(new CustomEvent(CONDITION_CHANGE, { bubbles: true })))
        .catch((error) => logger.error('Failed to rerender group element', error));
    },
  };
}

function createActivateFilterAction(groupConditionIds: ConditionId[], analysisContext: AnalysisContextPanel) {
  const canToggleDeactivationPerWorksheet = isDeactivatedIndeterminateForAny(groupConditionIds);
  const activatableConditions = activatableConditionsBulk(groupConditionIds);
  const canActivate = canActivateAllConditions(groupConditionIds);
  return {
    label: i18n('actions.filters.activate'),
    disabled: !canToggleDeactivationPerWorksheet && !canActivate,
    execute: () => {
      setConditionDeactivated(activatableConditions, false)
        .then(() => analysisContext.dispatchEvent(new CustomEvent(CONDITION_CHANGE, { bubbles: true })))
        .catch((error) => logger.error('Failed to rerender group element', error));
    },
  };
}

function createActivateFilterThisWorksheetAction(
  groupConditionIds: ConditionId[],
  analysisContext: AnalysisContextPanel,
) {
  const activatableConditions = activatableConditionsBulk(groupConditionIds);
  const canActivateWorksheetOnly = canActivateAllConditionsForActiveWorksheet(groupConditionIds);
  return {
    label: i18n('actions.filters.activate.thisWorksheet'),
    disabled: !canActivateWorksheetOnly,
    execute: () => {
      setConditionDeactivatedForActiveWorksheet(activatableConditions, false)
        .then(() => analysisContext.dispatchEvent(new CustomEvent(CONDITION_CHANGE, { bubbles: true })))
        .catch((error) => logger.error('Failed to rerender group element', error));
    },
  };
}

async function openAdvancedFilterSettingsDialog() {
  const { AdvancedFilterSettingsDialog } = await import(
    'cadenza/workbook/workbook-panel/analysis-context/advanced-filter-settings-dialog/advanced-filter-settings-dialog'
  );
  const hasChanges = await new AdvancedFilterSettingsDialog().connectAndShow();
  if (hasChanges) {
    getWorkbook().analysisContextPanel.dispatchEvent(new CustomEvent(CONDITION_CHANGE));
  }
}

customElements.define(COMPONENT_NAME, ConditionDropPanel);
